import { InfiniteData } from '@tanstack/react-query';

import { NonBenefitMedicine, NonBenefitMedicines, NonBenefitPriceSummary, NonF2fDoctor } from '@/models/nonBenefit';
import {
  NonBenefitMedicinesForSeoQuery,
  NonBenefitPriceTotalSummaryQuery,
  SearchNonF2fDoctorsForSeoByUserQuery,
} from '@/types/bff/gql-schema';

export const parseNonBenefitMedicinesForSeo = (data: NonBenefitMedicinesForSeoQuery) => {
  const medicines = data.nonBenefitMedicinesForSeo.map((nonBenefitMedicine) => {
    return new NonBenefitMedicine({
      id: nonBenefitMedicine.id,
      medicineName: nonBenefitMedicine.medicineName,
      medicineDescription: nonBenefitMedicine.medicineDescription ?? '',
      units: nonBenefitMedicine.units.map((unit) => {
        return {
          id: unit.id,
          unit: unit.unit,
          unitType: unit.unitType,
          unitTypeDisplayName: unit.unitTypeDisplayName,
        };
      }),
      treatment: {
        id: nonBenefitMedicine.treatment.id,
        name: nonBenefitMedicine.treatment.name,
        isBenefit: nonBenefitMedicine.treatment.isBenefit,
        categoryId: nonBenefitMedicine.treatment.categoryId,
      },
    });
  });
  return new NonBenefitMedicines(medicines);
};

export const parseNonBenefitPriceTotalSummary = (data: NonBenefitPriceTotalSummaryQuery): NonBenefitPriceSummary => {
  const nationalSummaries = data.nonBenefitPriceTotalSummary.nationalSummaries.map((nationalSummary) => ({
    averagePrice: nationalSummary.averagePrice,
    minPrice: nationalSummary.minPrice,
    treatmentMedicineUnitId: nationalSummary.treatmentMedicineUnitId,
    unitDisplayName: nationalSummary.unitDisplayName,
    unitTypeDisplayName: nationalSummary.unitTypeDisplayName,
  }));
  const regionSummaries = data.nonBenefitPriceTotalSummary.regionSummaries.map((regionSummary) => ({
    averagePrice: regionSummary.averagePrice,
    minPrice: regionSummary.minPrice,
    treatmentMedicineUnitId: regionSummary.treatmentMedicineUnitId,
    unitDisplayName: regionSummary.unitDisplayName,
    unitTypeDisplayName: regionSummary.unitTypeDisplayName,
  }));
  return new NonBenefitPriceSummary({
    regionSummaries,
    nationalSummaries,
  });
};

export const parseSearchNonF2fDoctorsForSeoByUser = (
  data: InfiniteData<SearchNonF2fDoctorsForSeoByUserQuery>,
): InfiniteData<NonF2fDoctor[]> => {
  return {
    ...data,
    pages: data.pages.map((page) => {
      return page.searchNonF2fDoctorsForSeoByUser.map((doctor) => {
        return new NonF2fDoctor({
          doctorId: doctor.doctorId,
          name: doctor.name,
          medicineType: doctor.medicineType,
          hospitalName: doctor.hospital.hospitalName,
          publicId: doctor.publicId,
          hospitalAddress: {
            jibunAddress: doctor.hospital.hospitalAddress.jibunAddress ?? '',
            roadAddress: doctor.hospital.hospitalAddress.roadAddress ?? '',
            regionByDepth: {
              siDoName: doctor.hospital.hospitalAddress.regionByDepth.siDoName,
              siGunGuName: doctor.hospital.hospitalAddress.regionByDepth.siGunGuName ?? '',
              legalEupMyeonDongName: doctor.hospital.hospitalAddress.regionByDepth.legalEupMyeonDongName,
            },
          },
          nonBenefitTreatments: doctor.nonBenefitTreatmentsV2.map((treatment) => ({
            nonBenefitTreatmentUnitInfos: treatment.nonBenefitTreatmentUnitInfos.map((unitInfo) => ({
              originalPrice: unitInfo.originalPrice,
              unitDisplayName: unitInfo.unitDisplayName,
              treatmentMedicineUnitId: unitInfo.treatmentMedicineUnitId,
            })),
          })),
          profileImageUrl: doctor.detail?.profileImageUrl ?? '',
          readyForVideoCall: doctor.readyForVideoCall,
          departments: doctor.departments,
        });
      });
    }),
  };
};
